import { useState, useEffect, useRef } from "react";
import PageLayout from "@/components/feature/PageLayout";
import DiagonalLines from "@/components/feature/DiagonalLines";
import DiagnosisStep from "./components/DiagnosisStep";
import DiagnosisResult from "./components/DiagnosisResult";

const Q1_OPTIONS = [
  { id: "q1_access", label: "アクセスや問い合わせが増えない", icon: "ri-bar-chart-line" },
  { id: "q1_cost", label: "販促費にお金を沢山掛けられない", icon: "ri-money-dollar-circle-line" },
  { id: "q1_design", label: "サイトやデザインが古く、ブランドイメージを損ねている", icon: "ri-layout-line" },
  { id: "q1_unknown", label: "何から手を付ければ良いか全く分からない", icon: "ri-question-line" },
];

const Q2_OPTIONS = [
  { id: "q2_sales", label: "とにかく売上・お問い合わせを増やしたい", icon: "ri-line-chart-line" },
  { id: "q2_awareness", label: "新しい層への認知を爆発的に広げたい", icon: "ri-megaphone-line" },
  { id: "q2_repeat", label: "リピーターへの定着率を上げたい", icon: "ri-user-heart-line" },
  { id: "q2_brand", label: "長期的なブランディングと信頼を確立したい", icon: "ri-award-line" },
];

type Step = 1 | 2 | "result";

export default function DiagnosisPage() {
  const [step, setStep] = useState<Step>(1);
  const [q1, setQ1] = useState<string | null>(null);
  const [q2, setQ2] = useState<string | null>(null);
  const [stepVisible, setStepVisible] = useState(true);
  const [counting, setCounting] = useState(false);
  const [countNum, setCountNum] = useState(0);
  const topRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setStepVisible(true), 80);
    return () => clearTimeout(t);
  }, [step]);

  const goToStep = (next: Step) => {
    setStepVisible(false);
    setTimeout(() => {
      setStep(next);
      // Smooth scroll to top of page without jump
      const top = topRef.current?.getBoundingClientRect().top ?? 0;
      const offset = window.scrollY + top;
      if (offset > window.scrollY + 10) {
        window.scrollTo({ top: offset - 80, behavior: "smooth" });
      }
    }, 350);
  };

  const handleQ1Next = () => {
    if (!q1) return;
    goToStep(2);
  };

  const handleQ2Next = () => {
    if (!q2) return;
    // Counting animation before result
    setStepVisible(false);
    setCounting(true);
    let n = 0;
    const iv = setInterval(() => {
      n += Math.floor(Math.random() * 12) + 3;
      if (n >= 100) { n = 100; clearInterval(iv); }
      setCountNum(n);
    }, 40);
    setTimeout(() => {
      setCounting(false);
      setStep("result");
      const top = topRef.current?.getBoundingClientRect().top ?? 0;
      const offset = window.scrollY + top;
      if (offset > window.scrollY + 10) {
        window.scrollTo({ top: offset - 80, behavior: "smooth" });
      }
      setTimeout(() => setStepVisible(true), 80);
    }, 1800);
  };

  const handleRetry = () => {
    setQ1(null);
    setQ2(null);
    goToStep(1);
  };

  return (
    <PageLayout>
      <style>{`
        @keyframes optIn {
          from { opacity: 0; transform: translateX(-16px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes countPulse {
          0%,100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>

      <div ref={topRef} className="min-h-screen" style={{ background: "#ffffff" }}>
        {/* Hero header */}
        <div
          className="relative overflow-hidden"
          style={{
            background: "linear-gradient(135deg, #faf8f4 0%, #f5f0e8 50%, #faf8f4 100%)",
            paddingTop: "120px",
            paddingBottom: "80px",
            borderBottom: "1px solid rgba(192,0,0,0.15)",
          }}
        >
          {/* Subtle golden grid lines */}
          {[16.6, 33.3, 50, 66.6, 83.3].map((pct, i) => (
            <div
              key={i}
              className="absolute top-0 bottom-0 pointer-events-none"
              style={{ left: `${pct}%`, width: "1px", background: "rgba(192,0,0,0.08)" }}
            />
          ))}
          {/* Large background watermark */}
          <div
            className="absolute pointer-events-none select-none hidden md:block"
            style={{
              fontFamily: "'Barlow', sans-serif",
              fontWeight: 700,
              fontSize: "clamp(100px, 16vw, 220px)",
              lineHeight: 1,
              letterSpacing: "-0.04em",
              color: "transparent",
              WebkitTextStroke: "1.5px rgba(192,0,0,0.12)",
              right: "-20px",
              bottom: "-30px",
              zIndex: 0,
              userSelect: "none",
            }}
          >
            DIAGNOSIS
          </div>
          <div className="relative px-8 md:px-20 z-10">
            <p
              className="mb-4 flex items-center gap-3"
              style={{ fontFamily: "'Barlow', sans-serif", fontSize: "12px", letterSpacing: "0.45em", color: "#c00000" }}
            >
              <span style={{ width: "24px", height: "1px", background: "#c00000", display: "inline-block" }} />
              FREE DIAGNOSIS
            </p>
            <div style={{ height: "1px", background: "linear-gradient(to right, #c00000, rgba(192,0,0,0.1))", marginBottom: "24px", maxWidth: "280px" }} />
            <h1
              style={{
                fontFamily: "'Barlow', sans-serif",
                fontSize: "clamp(32px, 5vw, 56px)",
                fontWeight: 700,
                letterSpacing: "-0.03em",
                lineHeight: 1.0,
                color: "#111111",
                marginBottom: "16px",
              }}
            >
              無料診断
            </h1>
            <p
              style={{
                fontFamily: "'Noto Sans JP', sans-serif",
                fontSize: "14px",
                color: "#111111",
                lineHeight: 1.9,
              }}
            >
              2つの質問に答えるだけで、あなたのビジネスに最適な<br className="hidden md:block" />
              マーケティング施策をご提案します。
            </p>
          </div>
        </div>

        {/* Main content */}
        <div className="px-8 md:px-20 py-16 md:py-24" style={{ background: "linear-gradient(to bottom, #ffffff 0%, #faf8f4 100%)" }}>
          <div className="max-w-2xl mx-auto">

            {/* Counting animation */}
            {counting && (
              <div className="flex flex-col items-center justify-center py-24 gap-8">
                <div className="relative w-32 h-32">
                  <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="44" fill="none" stroke="#e8e8e8" strokeWidth="4" />
                    <circle
                      cx="50" cy="50" r="44"
                      fill="none"
                      stroke="#c00000"
                      strokeWidth="4"
                      strokeLinecap="round"
                      strokeDasharray={`${2 * Math.PI * 44}`}
                      strokeDashoffset={`${2 * Math.PI * 44 * (1 - countNum / 100)}`}
                      style={{ transition: "stroke-dashoffset 0.04s linear" }}
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span
                      style={{
                        fontFamily: "'DM Mono', monospace",
                        fontSize: "28px",
                        fontWeight: 500,
                        color: "#111111",
                        letterSpacing: "0.05em",
                      }}
                    >
                      {countNum}
                    </span>
                  </div>
                </div>
                <div className="text-center">
                  <p style={{ fontFamily: "'Noto Sans JP', sans-serif", fontSize: "14px", color: "#111111" }}>
                    診断結果を分析中...
                  </p>
                  <p style={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", letterSpacing: "0.3em", color: "#111111", marginTop: "6px" }}>
                    ANALYZING YOUR ANSWERS
                  </p>
                </div>
              </div>
            )}

            {/* Step 1 */}
            {step === 1 && !counting && (
              <DiagnosisStep
                stepNum={1}
                totalSteps={2}
                question="現在、1番の「お悩み」はどれですか？"
                subText="あなたのビジネスが今、最も直面している課題を選んでください。"
                options={Q1_OPTIONS}
                selected={q1}
                onSelect={setQ1}
                onNext={handleQ1Next}
                visible={stepVisible}
              />
            )}

            {/* Step 2 */}
            {step === 2 && !counting && (
              <DiagnosisStep
                stepNum={2}
                totalSteps={2}
                question="Webを活用して、一番「達成したいこと」は何ですか？"
                subText="あなたのビジネスが目指す、最も重要なゴールを選んでください。"
                options={Q2_OPTIONS}
                selected={q2}
                onSelect={setQ2}
                onNext={handleQ2Next}
                visible={stepVisible}
              />
            )}

            {/* Result */}
            {step === "result" && !counting && (
              <DiagnosisResult
                q1={q1 ?? "q1_unknown"}
                q2={q2 ?? "q2_sales"}
                onRetry={handleRetry}
              />
            )}
          </div>
        </div>
      </div>
      <DiagonalLines variant="gold" intensity="light" />
    </PageLayout>
  );
}
